<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, ref, watch } from 'vue';

import KCheckboxField from '@/components/fields/KCheckboxField.vue';
import KTextField from '@/components/fields/KTextField.vue';
import KTextareaField from '@/components/fields/KTextareaField.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import JobIdAndIconLinkUnit from '@/components/units/JobIdAndIconLinkUnit.vue';
import useTable from '@/composables/useTable';

import tableStyles from '@/styles/table.module.scss';

export interface SmsDailyTableModel {
  select: Record<string, boolean>;
}

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<openapi.SMSDailyListItem[]>;
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const model = defineModel<SmsDailyTableModel>({
  default: () => ({ select: {} }),
});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<openapi.SMSDailyListItem[]>>
>;

const isCommonCheckbox = ref(false);
const isCommonCheckboxIndeterminate = ref(false);
const headersData = computed<HeadersData>(function () {
  return [
    { value: 'rockhopper_id', title: 'Job ID', className: 'jobIdTh' },
    { value: 'stops[0].state', title: 'State', className: 'stateTh' },
    { value: 'stops[0].name', title: 'Stop 1', className: 'stopTh' },
    { value: 'stops[1].name', title: 'Stop 2', className: 'stopTh' },
    { value: 'driver.name', title: 'Driver', className: 'driverTh' },
    {
      value: 'driver.offsider',
      title: 'Offsider',
      isNotSortable: true,
      className: 'offsiderTh',
    },
    {
      value: 'driver.extra_number',
      title: 'Extra number',
      isNotSortable: true,
      className: 'extraNumberTh',
    },
    {
      value: 'sms_message',
      title: 'Message',
      isNotSortable: true,
      className: 'messageTh',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

const allIds = computed(() => {
  return props.data.map((item) => item.job_id);
});

const selectCount = computed(() => {
  let _selectCount = 0;
  allIds.value.forEach((id: string) => {
    if (model.value.select[id]) _selectCount++;
  });
  return _selectCount;
});

function clearSelect() {
  for (const id of Object.getOwnPropertyNames(model.value.select)) {
    model.value.select[id] = false;
  }
}

function handleIsCommonCheckboxChange(): void {
  if (isCommonCheckbox.value) {
    setTimeout(() => {
      allIds.value.forEach((id: string) => {
        model.value.select[id] = true;
      });
    }, 0);
  } else {
    setTimeout(() => {
      clearSelect();
    }, 0);
  }
}

watch(
  () => model.value.select,
  () => {
    if (!selectCount.value) {
      isCommonCheckbox.value = false;
      isCommonCheckboxIndeterminate.value = false;
    } else if (selectCount.value === allIds.value.length) {
      isCommonCheckbox.value = true;
      isCommonCheckboxIndeterminate.value = false;
    } else {
      isCommonCheckbox.value = false;
      isCommonCheckboxIndeterminate.value = true;
    }
  },
  { deep: true },
);
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.hovered,
      tableStyles.stickyHeader,
      $style.smsDailyTable,
    ]"
    data-testid="smsDailyTable"
  >
    <thead>
      <tr>
        <th :class="$style.checkboxTh">
          <label :class="$style.checkboxLabel">
            <KCheckboxField
              v-model="isCommonCheckbox"
              :indeterminate="isCommonCheckboxIndeterminate"
              hide-details
              size="sm"
              :disabled="!sortedData.length"
              class="my-n2"
              @change="handleIsCommonCheckboxChange"
            />
          </label>
        </th>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="
              headerItem.className ? $style[headerItem.className] : undefined
            "
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.rockhopper_id">
        <tr>
          <td :class="$style.checkboxTd">
            <label :class="$style.checkboxLabel">
              <KCheckboxField
                v-model="model.select[dataItem.job_id]"
                hide-details
                size="sm"
                class="my-n2"
              />
            </label>
          </td>
          <td :class="$style.jobIdTd">
            <JobIdAndIconLinkUnit
              :job-id="parseInt(dataItem.job_id)"
              :job-date="dataItem.job_date"
            />
          </td>
          <td :class="[$style.stateTd, 'whitespace-nowrap']">
            <EmDashPlaceholder :item="dataItem.stops?.[0]?.state" />
          </td>
          <td :class="$style.stopTd">
            <EmDashPlaceholder :item="dataItem.stops?.[0]?.name" />
          </td>
          <td :class="$style.stopTd">
            <EmDashPlaceholder :item="dataItem.stops?.[1]?.name" />
          </td>
          <td :class="$style.driverTd">
            <EmDashPlaceholder
              v-if="dataItem.driver"
              :item="`${dataItem.driver.code}, ${dataItem.driver.name}`"
            />
          </td>
          <td :class="$style.offsiderTd">
            <KTextField
              v-model="dataItem.offsider"
              label="Offsider"
              :disabled="props.loading"
              hide-details
              density="compact"
              :class="$style.inputField"
            />
          </td>
          <td :class="$style.extraNumberTd">
            <KTextField
              v-model="dataItem.extra_number"
              label="Extra number"
              :disabled="props.loading"
              hide-details
              density="compact"
              :class="$style.inputField"
            />
          </td>
          <td :class="$style.messageTd">
            <KTextareaField
              v-if="dataItem.sms_message"
              v-model="dataItem.sms_message.content"
              label="Message"
              :disabled="props.loading"
              hide-details
              :rows="4"
              :class="$style.messageField"
            />
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/table.module.scss' as tableStyles;

.smsDailyTable {
  width: 100%;

  & > thead > tr > th {
    white-space: nowrap;
  }

  & > tbody > tr > td {
    vertical-align: top;
  }
}

.checkboxTh {
  min-width: 44px;
  width: 44px;
  padding: 0 !important;
  text-align: center;
}

.checkboxTd {
  padding: 0 !important;
  text-align: center;
}

.checkboxLabel {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
}

.jobIdTh {
  @include tableStyles.column-small;
}

.stateTh {
  min-width: 80px;
  width: 80px;
}





.stopTh,
.driverTh {
  @include tableStyles.column-wide;
}

.offsiderTh,
.extraNumberTh {
  @include tableStyles.column-wide;
}

.messageTh {
  min-width: 200px;
}

.jobIdTd {
  @include tableStyles.column-small;
  white-space: nowrap;
}

.stateTd {
  min-width: 80px;
}

.stopTd,
.driverTd {
  @include tableStyles.column-wide;
}

.offsiderTd,
.extraNumberTd {
  @include tableStyles.column-wide;
}

.messageTd {
  min-width: 200px;
}
</style>
